import { ILinkAccountCustomer, ILoyaltyCustomer } from '../interface/customer.interfaces';
import { AxiosResponse } from 'axios';
import Cookies from 'js-cookie';
import {
  Buyer,
  CheckoutPayload,
  CheckoutResponse,
  CheckVinByPoliceRegNoResponse,
  CityRegion,
  GetMerchantsInfoResponse,
  GlobalCart,
  ICompatibleVehicle,
  IExpeditionList,
  IVehicleListResponse,
  ITIntouchVehicleInfoResponse,
  LastAfterSalesOrder,
  PostBuyerPayload,
  PostGlobalCartPayload,
  PostGlobalCartResponse,
  preCheckoutPayload,
  preCheckoutResponse,
  ProductOrder,
  Region,
  TRespRecommendItem,
  VehicleMaster,
} from '../interface/order-placement.interface';
import { apiWithRefreshToken, api } from 'ui/services';
import { IHoliday } from '../interface/booking-service.interface';
import { extractFromCookies } from '../helpers/utils';
import { APIProxy } from './api-proxy';

const BASEPATH_URL = process.env.NEXT_PUBLIC_BASEPATH_URL;
const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL;
const isDev = process.env.NODE_ENV === 'development';

const getGlobalCart = async (globalCartPage = false): Promise<{ data: GlobalCart } | undefined> => {
  const guid = Cookies.get('guid');
  const path = isDev ? '/api/global-cart' : 'global-cart';
  const response: AxiosResponse<GlobalCart> = await api.get(path, {
    baseURL: isDev ? BASEPATH_URL : BASE_URL,
    params: {
      guid: guid || undefined,
      doUpdateHiddenEntries: globalCartPage,
    },
  });
  if (response?.data?.guid) Cookies.set('guid', response.data.guid, { expires: 7 });

  return {
    ...response,
    data: {
      ...response.data,
      entries: response?.data?.entries ? [...response.data.entries].reverse() : [],
    },
  };
};

const postGlobalCart = async (payload: PostGlobalCartPayload) => {
  const guid = Cookies.get('guid');
  const path = isDev ? '/api/global-cart' : 'global-cart';
  const response: AxiosResponse<PostGlobalCartResponse> = await api.post(path, null, payload, {
    baseURL: isDev ? BASEPATH_URL : BASE_URL,
    params: {
      guid: guid || undefined,
    },
  });
  if (response?.data?.guid) {
    Cookies.set('guid', response.data.guid, { expires: 7 });
  }
  return response;
};

const getLastAfterSalesOrder = async (): Promise<
  AxiosResponse<LastAfterSalesOrder> | undefined
> => {
  const guid = Cookies.get('guid');
  if (!guid) return undefined;

  const response: AxiosResponse<LastAfterSalesOrder> = await api.get('/api/last-after-sales-order', {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    params: { guid },
  });
  return response;
};

const getBuyerList = async () => {
  const response: AxiosResponse<Buyer[]> = await api.get('/api/buyers', {
    baseURL: BASEPATH_URL,
  });
  return response;
};

const postBuyer = async (payload: PostBuyerPayload) => {
  const response: AxiosResponse = await api.post('/api/buyer', null, payload, {
    baseURL: BASEPATH_URL,
  });
  return response;
};

const editBuyer = async (payload: PostBuyerPayload) => {
  const response: AxiosResponse = await api.put('/api/buyer', {}, payload, {
    baseURL: BASEPATH_URL,
  });
  return response;
};

const deleteBuyer = async (buyerId: string) => {
  const response: AxiosResponse = await api.deleteData(
    `${BASEPATH_URL}/api/buyer`,
    { buyerId: buyerId },
    true,
    true,
    false
  );
  return response;
};

async function getAllCities() {
  const response: AxiosResponse<{ cities: CityRegion[] }> = await api.get(
    '/content/v1/city/getAllCities',
    { baseURL: BASE_URL }
  );
  return response.data;
}

async function getDistrictByCity(cityIsocode: string) {
  const response: AxiosResponse<{ districts: Region[] }> = await api.get(
    '/saphybris-proxy/v1/saphybris/getDistrictByCity',
    {
      baseURL: BASE_URL,
      params: {
        code: cityIsocode,
      },
    }
  );
  return response.data;
}

async function getSubdistrictByDistrict(districtIsocode: string) {
  const response: AxiosResponse<{ subDistricts: Region[] }> = await api.get(
    '/saphybris-proxy/v1/saphybris/getSubdistrictByDistrict',
    {
      baseURL: BASE_URL,
      params: {
        code: districtIsocode,
      },
    }
  );
  return response.data;
}

const getProducts = async (
  category: string,
  carName?: string,
  size = 4
): Promise<TRespRecommendItem> => {
  const res = await api.get('product/v1/products', {
    params: {
      category,
      pageSize: size,
      query: carName ? `:name-asc:carModel:${carName}` : undefined,
    },
  });
  return res?.data.products;
};

export async function getBranchHolidays({
  ...rest
}: {
  branchCode: string;
  isGlobalHoliday: boolean;
  withWorkHours: boolean;
}) {
  const response: AxiosResponse<{ auto2000HolidayCalendar: IHoliday[] }> = await api.get(
    '/service-booking/v1/branch/holiday',
    {
      params: {
        ...rest,
      },
    }
  );

  return response.data;
}

export async function getSlotsByBranchAndRange(data: {
  totalDuration: number;
  scheduleStartDate: string;
  scheduleEndDate: string;
  listOfBranch: string[];
  productCode: string[];
}) {
  const response: AxiosResponse<{
    availableSlot: { slotDate: string; slotTime: string[] }[];
  }> = await api.post(
    '/saphybris-proxy/v1/saphybris/getSlotsByBranchAndRange',
    null,
    { ...data },
    { baseURL: BASE_URL }
  );

  return response.data;
}

export async function postReserveSlot(data: {
  policeRegNo?: string;
  reserveSlotNo?: string;
  branchCode: string;
  startDateTime: string;
  productCode: string[];
  isPayment?: boolean;
}) {
  const response: AxiosResponse<{
    reserveSlotNo?: string;
    acknowledge: number;
    message: string;
    errorCode: number;
  }> = await api.post('/saphybris-proxy/v1/saphybris/reserve-booking-slot', null, data, {
    baseURL: BASE_URL,
  });

  return response.data;
}

export const getRecommendAccessories = async (
  carName?: string,
  size = 4
): Promise<TRespRecommendItem> => {
  return getProducts('ACCESSORIES', carName, size);
};

export const getRecommendServiceCoupon = async (
  carModel?: string,
  size = 4
): Promise<TRespRecommendItem> => {
  const res = await api.get('product/v1/products', {
    params: {
      category: 'COUPONSERVICES',
      pageSize: size,
      query: carModel ? `:name-asc:models:${carModel}` : undefined,
    },
  });
  return res?.data.products;
};

export const getRecommendServicePackage = async (
  carModel?: string,
  size = 4
): Promise<TRespRecommendItem> => {
  const res = await api.get('product/v1/products', {
    params: {
      category: 'SERVICES',
      pageSize: size,
      query: carModel ? `:name-asc:models:${carModel}` : undefined,
    },
  });
  return res?.data.products;
};

export const addToCart = async (productCode: string, quantity: number) => {
  const guid = Cookies.get('guid');
  if (guid) {
    const {
      data: { entries },
    } = await getGlobalCart();
    const currentProducts = entries.map((entry) => ({
      code: entry.product.code,
      quantity: entry.quantity,
    }));
    //
    let existingProduct = currentProducts.find((product) => product.code === productCode);
    let qty = quantity;
    if (existingProduct) {
      qty += existingProduct.quantity;
    } else {
      existingProduct = { code: productCode, quantity: qty };
    }
    //
    return postGlobalCart({
      products: [{ ...existingProduct, quantity: qty }],
    });
  } else {
    return postGlobalCart({ products: [{ code: productCode, quantity }] });
  }
};

const getRedirectUrlAstrapay = async (guid: string): Promise<ILinkAccountCustomer> => {
  try {
    const response = await api.get(`/api/linking-account?guid=${guid}`, {
      baseURL: BASEPATH_URL,
    });
    return response?.data;
  } catch (err) {
    console.log(err);
    return err;
  }
};

const getBalanceAstrapay = async (device: string): Promise<ILoyaltyCustomer | string> => {
  try {
    const response = await api.get(`/api/get-balance-astrapay?device=${device}`, {
      baseURL: BASEPATH_URL,
    });
    return response?.data;
  } catch (err) {
    console.log(err);
    return err;
  }
};

const preCheckoutGlobalCart = async (
  guid: string,
  payload: preCheckoutPayload,
  token = null
): Promise<preCheckoutResponse | undefined> => {
  try {
    const response = await apiWithRefreshToken.post(
      token ? `/api/precheckout?token=${token}` : `/api/precheckout`,
      {
        guid,
        payload,
      },
      {
        baseURL: BASEPATH_URL,
      }
    );
    return response.data;
  } catch (err) {
    console.error(err);
  }
};

const getMerchantsInfo = async (req?, res?): Promise<GetMerchantsInfoResponse | undefined> => {
  if (req && res) {
    const apiProxy = new APIProxy(req, res);
    const resp = await apiProxy.makeRequest({
      method: req.method!,
      baseURL: BASE_URL,
      url: `saphybris-proxy/v1/saphybris/merchants-info`,
    });
    return resp.data;
  } else {
    try {
      const resp = await api.get('saphybris-proxy/v1/saphybris/merchants-info', {
        baseURL: BASE_URL,
      });
      return resp.data;
    } catch (err) {
      console.error({ err });
    }
  }
};

const checkoutGlobalCart = async (
  guid: string,
  payload: CheckoutPayload
): Promise<{ data: CheckoutResponse } | undefined> => {
  try {
    const response = await apiWithRefreshToken.post(
      `/api/checkout`,
      {
        guid,
        payload,
      },
      {
        baseURL: BASEPATH_URL,
      }
    );
    return response;
  } catch (err) {
    console.error(err);
  }
};

const getExpedition = async (cartId: string, subDistrictIsoCode: string) => {
  const response = await api.get(`/api/services/get-expedition`, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    params: {
      cartCode: cartId,
      customerSubDistrict: subDistrictIsoCode,
    },
  });

  return response.data as { data: IExpeditionList[] };
};

const getCompatibleVehicleList = async (productCode: string): Promise<IVehicleListResponse> => {
  const response = await api.get(
    `/api/get-compatible-vehicle-list?productCode=${productCode}`,
    {
      baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    }
  );
  return response.data;
};

const getVehicleMaster = async (param?: { modelCode?: string }) => {
  const response: AxiosResponse<VehicleMaster> = await api.get(
    '/service-booking/v1/vehicles/vehicle-master',
    {
      params: {
        modelName: param?.modelCode || undefined,
      },
    }
  );

  return response.data;
};

const reserveSlotsForProducts = async (products: ProductOrder[], branchCode: string) => {
  const promises = products
    .filter((product) => product?.dataDetails?.bookingDate && product?.dataDetails?.reserveSlotNo)
    .map(async (product) => {
      return postReserveSlot({
        branchCode,
        startDateTime: product.dataDetails.bookingDate,
        productCode: [product.code],
        reserveSlotNo: product.dataDetails?.reserveSlotNo,
        policeRegNo: product.dataDetails?.licensePlateNumber,
        isPayment: true,
      }).catch((error) => ({
        error,
        productCode: product.code,
      }));
    });

  const results = await Promise.allSettled(promises);

  return results;
};

const checkVinbyPoliceRegNo = async (
  policeRegNo: string
): Promise<CheckVinByPoliceRegNoResponse | undefined> => {
  try {
    const res = await api.get(`/api/check-vin-by-police-reg-no?policeRegNo=${policeRegNo}`, {
      baseURL: BASEPATH_URL,
    });
    return res.data;
  } catch (err) {
    console.error({ err });
  }
};

const linkingAccountRequest = async (context, guid: string): Promise<ILinkAccountCustomer> => {
  const accessToken = extractFromCookies('access-token', context?.req?.headers?.cookie);
  try {
    const apiProxy = new APIProxy(context.req, context.res);
    const response = await apiProxy.makeRequest({
      method: 'GET',
      baseURL: BASE_URL,
      url: `saphybris-proxy/v1/saphybris/astrapay/linking-account`,
      params: { cartGuid: guid },
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return response?.data;
  } catch (error) {
    console.log(error);
  }
};

interface ICheckTIntouchVehicleVINStatusPayload {
  vin: string;
  newPackageCode: string;
  partnerCode: string;
}

const getCheckTIntouchVehicleVINStatus = async (
  payload: ICheckTIntouchVehicleVINStatusPayload
): Promise<ITIntouchVehicleInfoResponse> => {
  try {
    const response = await apiWithRefreshToken.post('tintouch-vehicle-information', payload, {
      baseURL: BASE_URL,
    });
    return response.data;
  } catch (err) {
    console.error(err);
  }
};

export {
  getGlobalCart,
  postGlobalCart,
  getLastAfterSalesOrder,
  getBuyerList,
  getAllCities,
  getDistrictByCity,
  getSubdistrictByDistrict,
  postBuyer,
  editBuyer,
  deleteBuyer,
  getRedirectUrlAstrapay,
  getBalanceAstrapay,
  preCheckoutGlobalCart,
  getMerchantsInfo,
  checkoutGlobalCart,
  getExpedition,
  getCompatibleVehicleList,
  getVehicleMaster,
  reserveSlotsForProducts,
  checkVinbyPoliceRegNo,
  linkingAccountRequest,
  getCheckTIntouchVehicleVINStatus,
};
